/**
 * researchTaskService.js
 * Singleton service that manages async task polling and localStorage persistence.
 *
 * Usage:
 *   import taskService from '@/services/researchTaskService';
 *   const taskId = await taskService.submit('journal_write', payload, '/api/task-runs');
 *   taskService.on('task:completed', handler);
 *   taskService.on('task:failed',    handler);
 *   taskService.off('task:completed', handler);
 */

import axios from 'axios';
import { API_BASE_URL } from '@/config/api.js';
import logger from '@/services/logger';

const STORAGE_KEY = 'papernova_tasks';
const POLL_INTERVAL = 3000;   // ms between polls
const MAX_AGE_MS    = 24 * 60 * 60 * 1000; // purge tasks older than 24 h
const JOURNAL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

class TaskService {
  constructor() {
    this._tasks    = {};   // taskId -> task object
    this._timers   = {};   // taskId -> intervalId
    this._polling = new Set();
    this._handlers = {};   // eventName -> Set<fn>
    this._syncPromise = null;
    this._syncOwner = null;
    this._syncScope = null;
    this._storageOwner = this._currentStorageOwner();
    this._ownerGeneration = 0;
    this._load();
    this._resumePolling();
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Submit an async job.
   * @param {string} label     - Human-readable label for the notification bar
   * @param {object} payload   - POST body (or null for GET)
   * @param {string} endpoint  - Backend async endpoint URL
   * @param {string} method    - 'POST' (default) or 'GET'
   * @returns {Promise<string>} task_id
   */
  async submit(label, payload, endpoint, method = 'POST') {
    this._switchStorageOwnerIfNeeded();
    const requestOwner = this._storageOwner;
    const requestGeneration = this._ownerGeneration;
    const token = localStorage.getItem('jwt_token');
    const cfg   = { headers: { Authorization: `Bearer ${token}` } };

    let resp;
    if (method === 'GET') {
      resp = await axios.get(`${API_BASE_URL}${endpoint}`, cfg);
    } else {
      resp = await axios.post(`${API_BASE_URL}${endpoint}`, payload, cfg);
    }
    if (this._storageOwner !== requestOwner
      || this._ownerGeneration !== requestGeneration
      || this._currentStorageOwner() !== requestOwner) {
      throw new Error('Session changed while the task was being submitted');
    }

    const { task_id, task_type, message } = resp.data;
    if (!task_id) throw new Error('Backend did not return task_id');

    const task = {
      task_id,
      task_type: task_type || 'unknown',
      client_context: typeof payload?.client_context === 'string' ? payload.client_context : null,
      label:     label || task_type || 'Task',
      status:    'pending',
      progress:  0,
      progress_message: message || 'Starting…',
      result:    null,
      error:     null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    this._tasks[task_id] = task;
    this._save();
    this._startPolling(task_id);
    this._emit('task:created', task);
    return task_id;
  }

  /** Get a single task snapshot. */
  getTask(taskId) {
    return this._tasks[taskId] || null;
  }

  /** Short actions stay in their card; durable execution survives leaving it. */
  async runAction(endpoint, payload, label, { signal } = {}) {
    this._switchStorageOwnerIfNeeded();
    const owner = this._storageOwner;
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(payload)));
    const actionKey = `${endpoint}:${Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('')}`;
    let task = this.getAllTasks().find(item => item.actionKey === actionKey && item.awaitingResult && item.status !== 'failed');
    if (!task) {
      const { data } = await axios.post(`${API_BASE_URL}${endpoint}`, { ...payload, background: true }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwt_token')}` }, signal,
      });
      if (this._currentStorageOwner() !== owner) throw new Error('登录状态已改变');
      if (!data.task_id) return { data };
      task = { task_id: data.task_id, task_type: data.task_type, label, source: 'legacy', metadata: data.metadata,
        result_route: data.metadata?.result_route,
        status: 'pending', progress: 0, created_at: new Date().toISOString(), actionKey, awaitingResult: true };
      this._tasks[task.task_id] = task;
      this._save();
      this._startPolling(task.task_id);
    }
    return new Promise((resolve, reject) => {
      const finish = (current) => {
        if (current?.task_id !== task.task_id || !['completed', 'failed'].includes(current.status)) return;
        cleanup();
        current.awaitingResult = false;
        this._save();
        if (current.status === 'failed') reject(new Error(current.error || '任务执行失败'));
        else resolve({ data: current.result });
      };
      const abort = () => { cleanup(); reject(new DOMException('页面已离开，任务继续运行', 'AbortError')); };
      const cleanup = () => {
        this.off('task:updated', finish); this.off('task:completed', finish); this.off('task:failed', finish);
        signal?.removeEventListener('abort', abort);
      };
      this.on('task:updated', finish); this.on('task:completed', finish); this.on('task:failed', finish);
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) abort();
      else { finish(task); if (task.awaitingResult) void this._poll(task.task_id); }
    });
  }

  /** Get all tracked tasks, most-recent first. */
  getAllTasks() {
    this._switchStorageOwnerIfNeeded();
    return Object.values(this._tasks)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  }

  /** Hydrate local history from both server-backed task stores. */
  async syncFromServer({ activeOnly = false } = {}) {
    const token = localStorage.getItem('jwt_token');
    if (!token) return this.getAllTasks();
    this._switchStorageOwnerIfNeeded();
    const requestOwner = this._storageOwner;
    const requestGeneration = this._ownerGeneration;
    const syncScope = activeOnly ? 'active' : 'full';
    if (this._syncPromise && this._syncOwner === requestOwner) {
      if (this._syncScope === syncScope || this._syncScope === 'full') return this._syncPromise;
      return this._syncPromise
        .catch(() => null)
        .then(() => this.syncFromServer({ activeOnly: false }));
    }
    const isCurrentRequest = () => (
      this._storageOwner === requestOwner
      && this._ownerGeneration === requestGeneration
      && this._currentStorageOwner() === requestOwner
    );

    const cfg = { headers: { Authorization: `Bearer ${token}` } };
    let syncPromise;
    syncPromise = Promise.allSettled([
      axios.get(`${API_BASE_URL}/api/tasks`, {
        ...cfg,
        params: activeOnly ? { limit: 20, active_only: 1, compact: 1 } : { limit: 100 },
      }),
      axios.get(`${API_BASE_URL}/api/task-runs`, {
        ...cfg,
        params: activeOnly
          ? { limit: 20, project_type: 'journal', active_only: 1, compact: 1 }
          : { limit: 200, project_type: 'journal' },
      }),
    ]).then(([legacyResult, runResult]) => {
      if (!isCurrentRequest()) return this.getAllTasks();
      const syncErrors = [];
      if (legacyResult.status === 'fulfilled') {
        const remoteTasks = legacyResult.value.data?.tasks || [];
        const remoteTaskIds = new Set(remoteTasks.map(task => String(task.task_id)));
        remoteTasks.forEach((remote) => {
          const previous = this._tasks[remote.task_id] || {};
          const metadata = { ...(previous.metadata || {}), ...(remote.metadata || {}) };
          const merged = {
            ...previous,
            ...remote,
            source: 'legacy',
            metadata,
            client_context: previous.client_context || metadata.client_context || null,
            label: metadata.display_label || previous.label || remote.task_type || 'Task',
            result_route: metadata.result_route || previous.result_route,
          };
          this._tasks[remote.task_id] = merged;
          if (['pending', 'running'].includes(merged.status)) this._startPolling(remote.task_id);
        });
        if (!activeOnly) Object.entries(this._tasks).forEach(([taskId, task]) => {
          if (task?.source === 'legacy' && !remoteTaskIds.has(String(taskId))) {
            this._stopPolling(taskId);
            delete this._tasks[taskId];
          }
        });
      } else {
        logger.warn('TaskService', 'Unable to sync background tasks', legacyResult.reason?.message);
        syncErrors.push(legacyResult.reason || new Error('Background task sync failed'));
      }

      if (runResult.status === 'fulfilled') {
        const taskRuns = runResult.value.data?.runs || [];
        taskRuns.forEach((run) => this.trackTaskRun(run, false));
        const durableRunIds = new Set(taskRuns.map(run => String(run.id)));
        const durableLegacyTaskIds = new Set(
          taskRuns.map(run => String(run.legacy_task_id || '')).filter(Boolean)
        );
        if (!activeOnly) Object.entries(this._tasks).forEach(([taskId, task]) => {
          const linkedRunId = String(
            task?.metadata?.run_id || task?.metadata?.persistent_run_id || ''
          );
          if (task?.source === 'legacy' && !task.actionKey && (
            durableLegacyTaskIds.has(String(taskId))
            || (linkedRunId && durableRunIds.has(linkedRunId))
          )) {
            this._stopPolling(taskId);
            delete this._tasks[taskId];
          }
          if (task?.source === 'task_run'
            && !durableRunIds.has(String(task.server_id || ''))) {
            this._stopPolling(taskId);
            delete this._tasks[taskId];
          }
        });
      } else {
        logger.warn('TaskService', 'Unable to sync journal task runs', runResult.reason?.message);
        syncErrors.push(runResult.reason || new Error('Journal task run sync failed'));
      }

      if (!isCurrentRequest()) return this.getAllTasks();
      this._save();
      this._emit('tasks:refreshed', this.getAllTasks());
      if (syncErrors.length) throw syncErrors[0];
      return this.getAllTasks();
    }).finally(() => {
      if (this._syncPromise === syncPromise) {
        this._syncPromise = null;
        this._syncOwner = null;
        this._syncScope = null;
      }
    });
    this._syncPromise = syncPromise;
    this._syncOwner = requestOwner;
    this._syncScope = syncScope;
    return syncPromise;
  }

  /** Add or update a durable Paperdoc journal run in the shared task store. */
  trackTaskRun(run, emit = true) {
    this._switchStorageOwnerIfNeeded();
    if (!run?.id || run.project_type !== 'journal') return null;
    const taskId = `run:${run.id}`;
    const priorEntries = Object.entries(this._tasks).filter(([, task]) => (
      task?.source === 'task_run' && String(task.server_id) === String(run.id)
    ));
    const linkedLegacyTaskId = String(run.legacy_task_id || '');
    const linkedLegacyTask = linkedLegacyTaskId ? this._tasks[linkedLegacyTaskId] : null;
    const previous = this._tasks[taskId] || priorEntries[0]?.[1] || linkedLegacyTask || {};
    priorEntries.forEach(([priorTaskId]) => {
      if (priorTaskId === taskId) return;
      this._stopPolling(priorTaskId);
      delete this._tasks[priorTaskId];
    });
    if (linkedLegacyTaskId && linkedLegacyTaskId !== taskId) {
      this._stopPolling(linkedLegacyTaskId);
      delete this._tasks[linkedLegacyTaskId];
    }
    const effectiveRunStatus = ['queued', 'running'].includes(run.status)
      && ['queued', 'running'].includes(run.execution_status)
      ? run.execution_status
      : run.status;
    const status = {
      queued: 'pending',
      running: 'running',
      awaiting_input: 'awaiting_input',
      succeeded: 'completed',
      failed: 'failed',
    }[effectiveRunStatus] || effectiveRunStatus;
    const metadata = {
      ...(previous.metadata || {}),
      project_id: run.project_id,
      project_name: run.project_name,
      project_type: run.project_type,
      branch_id: run.branch_id,
      idea_title: run.idea_title,
      sku: run.sku,
      task_position: run.task_position,
      client_context: 'journal_sop',
      visibility: run.task_visibility || previous.metadata?.visibility,
    };
    const label = run.sku === 'JRNL_MATCH'
      ? `${run.project_name || previous.metadata?.project_name || '期刊研究'} · 选题生成`
      : (run.task_name || run.sku || previous.label || '旗舰版任务');
    const task = {
      ...previous,
      task_id: taskId,
      server_id: run.id,
      legacy_task_id: run.legacy_task_id || null,
      source: 'task_run',
      task_type: run.sku === 'JRNL_MATCH' ? 'generate_ideas_advanced' : 'journal_task_run',
      label,
      status,
      progress: status === 'completed' ? 100 : Number(run.progress || 0),
      progress_message: run.progress_message || previous.progress_message || '',
      stage_token_usage: run.stage_token_usage || previous.stage_token_usage || {},
      result: run.result || previous.result || null,
      error: run.error || null,
      credits: run.credits,
      charge_mode: run.charge_mode || previous.charge_mode || null,
      created_at: run.created_at || previous.created_at || new Date().toISOString(),
      started_at: run.started_at || null,
      completed_at: run.completed_at || null,
      updated_at: run.completed_at || run.started_at || run.created_at || new Date().toISOString(),
      client_context: 'journal_sop',
      metadata,
      result_route: `/run/${encodeURIComponent(run.id)}?sop=1&return_to=${encodeURIComponent(`/projects/${run.project_id}?tab=tasks`)}`,
    };
    this._tasks[taskId] = task;
    if (['pending', 'running'].includes(status)) this._startPolling(taskId);
    else this._stopPolling(taskId);
    if (emit) {
      this._save();
      this._emit('task:updated', task);
    }
    return task;
  }

  isJournalTask(task) {
    return task?.task_type === 'generate_ideas_advanced'
      || task?.task_type === 'journal_task_run'
      || task?.client_context === 'journal_sop'
      || task?.metadata?.client_context === 'journal_sop';
  }

  /** Merge lightweight UI metadata into a locally tracked task. */
  setTaskMetadata(taskId, metadata = {}) {
    const task = this._tasks[taskId];
    if (!task || !metadata || typeof metadata !== 'object') return null;
    task.metadata = { ...(task.metadata || {}), ...metadata };
    if (typeof metadata.client_context === 'string') {
      task.client_context = metadata.client_context;
    }
    task.updated_at = new Date().toISOString();
    this._tasks[taskId] = task;
    this._save();
    this._emit('task:updated', task);
    return task;
  }

  /** Get only running/pending tasks. */
  getActiveTasks() {
    return this.getAllTasks().filter(t => ['pending', 'running'].includes(t.status) && t.metadata?.visibility !== 'local');
  }

  /** Remove a finished task from local store. */
  dismissTask(taskId) {
    delete this._tasks[taskId];
    this._save();
    this._emit('task:dismissed', { task_id: taskId });
  }

  /**
   * Cancel a running task.
   * Calls the backend cancel endpoint, then marks the task locally.
   * @param {string} taskId
   * @returns {Promise<boolean>} true if cancelled successfully
   */
  async cancelTask(taskId) {
    this._switchStorageOwnerIfNeeded();
    const task = this._tasks[taskId];
    if (!task) return false;
    const requestOwner = this._storageOwner;
    const requestGeneration = this._ownerGeneration;
    const isTaskRun = task.source === 'task_run' && task.server_id;
    const cancellableTaskId = isTaskRun
      ? task.server_id
      : (task.legacy_task_id || taskId);
    if (!cancellableTaskId) return false;

    // Try to cancel on the backend
    try {
      const token = localStorage.getItem('jwt_token');
      const response = await axios.post(
        `${API_BASE_URL}${isTaskRun ? `/api/task-runs/${cancellableTaskId}/cancel` : `/api/tasks/${cancellableTaskId}/cancel`}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (this._storageOwner !== requestOwner
        || this._ownerGeneration !== requestGeneration
        || this._currentStorageOwner() !== requestOwner) {
        return false;
      }
      if (response.status === 202) {
        task.progress_message = response.data.message || '正在停止';
        this._save();
        this._emit('task:updated', task);
        return true;
      }
    } catch (err) {
      if (this._storageOwner !== requestOwner
        || this._ownerGeneration !== requestGeneration
        || this._currentStorageOwner() !== requestOwner) {
        return false;
      }
      logger.warn('TaskService', '[TaskService] Backend cancel failed (task may already be gone):', err.message);
      await this._poll(taskId);
      const current = this._tasks[taskId];
      if (current && ['pending', 'running'].includes(current.status)) this._startPolling(taskId);
      return false;
    }

    this._stopPolling(taskId);
    task.status = 'failed';
    task.error = 'Cancelled by user';
    task.progress_message = 'Cancelled by user';
    task.updated_at = new Date().toISOString();
    this._tasks[taskId] = task;
    this._save();
    this._emit('task:failed', task);
    return true;
  }

  /** Clear all finished tasks. */
  clearFinished() {
    Object.keys(this._tasks).forEach(id => {
      if (['completed', 'failed'].includes(this._tasks[id].status)) {
        delete this._tasks[id];
      }
    });
    this._save();
    this._emit('tasks:cleared');
  }

  /** Subscribe to events. */
  on(event, fn) {
    if (!this._handlers[event]) this._handlers[event] = new Set();
    this._handlers[event].add(fn);
  }

  /** Unsubscribe. */
  off(event, fn) {
    if (this._handlers[event]) this._handlers[event].delete(fn);
  }

  // ─── Internal ───────────────────────────────────────────────────────────────

  _emit(event, data) {
    if (this._handlers[event]) {
      this._handlers[event].forEach(fn => {
        try { fn(data); } catch (e) { logger.error('TaskService', '[TaskService]', e); }
      });
    }
  }

  _startPolling(taskId) {
    if (this._timers[taskId]) return;  // already polling
    this._timers[taskId] = setInterval(() => this._poll(taskId), POLL_INTERVAL);
  }

  _stopPolling(taskId) {
    if (this._timers[taskId]) {
      clearInterval(this._timers[taskId]);
      delete this._timers[taskId];
    }
  }

  async _poll(taskId) {
    if (this._polling.has(taskId)) return;
    const token = localStorage.getItem('jwt_token');
    if (!token) { this._stopPolling(taskId); return; }
    this._switchStorageOwnerIfNeeded();
    const requestOwner = this._storageOwner;
    const requestGeneration = this._ownerGeneration;
    const isCurrentRequest = () => (
      this._storageOwner === requestOwner
      && this._ownerGeneration === requestGeneration
      && this._currentStorageOwner() === requestOwner
    );

    this._polling.add(taskId);
    try {
      const tracked = this._tasks[taskId];
      const isTaskRun = tracked?.source === 'task_run';
      const remoteId = isTaskRun ? tracked.server_id : taskId;
      const resp = await axios.get(
        `${API_BASE_URL}${isTaskRun ? `/api/task-runs/${remoteId}` : `/api/tasks/${remoteId}`}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!isCurrentRequest()) return;
      const remote = isTaskRun ? resp.data?.run : resp.data;

      if (isTaskRun) {
        if (!remote?.id) throw new Error('Task run response is missing run data');
        const previousStatus = tracked.status;
        const mergedRun = this.trackTaskRun(remote, false);
        this._save();
        this._emit('task:updated', mergedRun);
        if (mergedRun.status === 'completed' && previousStatus !== 'completed') {
          this._emit('task:completed', mergedRun);
        } else if (mergedRun.status === 'failed' && previousStatus !== 'failed') {
          this._emit('task:failed', mergedRun);
        }
        return;
      }

      const prev   = this._tasks[taskId];
      const merged = { ...(prev || {}), ...remote, result_route: remote.metadata?.result_route || prev?.result_route };
      // Preserve our label (backend doesn't store it)
      if (prev && prev.label) merged.label = prev.label;

      this._tasks[taskId] = merged;
      this._save();
      this._emit('task:updated', merged);

      if (merged.status === 'completed') {
        this._stopPolling(taskId);
        this._emit('task:completed', merged);
      } else if (merged.status === 'failed') {
        this._stopPolling(taskId);
        this._emit('task:failed', merged);
      }
    } catch (err) {
      if (!isCurrentRequest()) return;
      if (err.response && err.response.status === 404) {
        // Task gone from server (server restarted / task expired / cleaned up)
        this._stopPolling(taskId);
        const task = this._tasks[taskId];
        if (task && (task.status === 'pending' || task.status === 'running')) {
          task.status = 'failed';
          task.error = 'Task record not found on server — the server may have restarted. The task result may have been saved successfully. Please check your results page or try again.';
          task.progress_message = 'Task record lost (server may have restarted)';
          task.updated_at = new Date().toISOString();
          this._tasks[taskId] = task;
          this._save();
          this._emit('task:failed', task);
        }
      } else if (err.response && err.response.status === 403) {
        // Wrong user — stop polling silently
        this._stopPolling(taskId);
      }
    } finally { this._polling.delete(taskId); }
  }

  _resumePolling() {
    const now = Date.now();
    Object.values(this._tasks).forEach(t => {
      // Drop stale tasks
      const maxAge = this.isJournalTask(t) ? JOURNAL_MAX_AGE_MS : MAX_AGE_MS;
      if (new Date(t.created_at).getTime() + maxAge < now) {
        delete this._tasks[t.task_id];
        return;
      }
      // Re-poll tasks that were still running when the page was last open
      if (t.status === 'pending' || t.status === 'running') {
        this._startPolling(t.task_id);
      }
    });
    this._save();
  }

  _save() {
    try {
      localStorage.setItem(this._storageKey(), JSON.stringify(this._tasks));
    } catch (_) { /* quota exceeded – ignore */ }
  }

  _load() {
    try {
      const raw = localStorage.getItem(this._storageKey());
      this._tasks = raw ? JSON.parse(raw) : {};
    } catch (_) {
      this._tasks = {};
    }
  }

  _currentStorageOwner() {
    const token = localStorage.getItem('jwt_token') || '';
    try {
      const raw = token.split('.')[1] || '';
      const encoded = raw.replace(/-/g, '+').replace(/_/g, '/')
        .padEnd(Math.ceil(raw.length / 4) * 4, '=');
      return String(JSON.parse(atob(encoded)).sub || 'anonymous');
    } catch (_) {
      return 'anonymous';
    }
  }

  _storageKey() {
    return `${STORAGE_KEY}:${this._storageOwner}`;
  }

  _switchStorageOwnerIfNeeded() {
    const owner = this._currentStorageOwner();
    if (owner === this._storageOwner) return;
    Object.keys(this._timers).forEach((taskId) => this._stopPolling(taskId));
    this._ownerGeneration += 1;
    this._syncPromise = null;
    this._syncOwner = null;
    this._storageOwner = owner;
    this._tasks = {};
    this._load();
    this._resumePolling();
  }
}

// Singleton
const taskService = new TaskService();
export default taskService;
